import { useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { QRCodeSVG } from 'qrcode.react'

import { Button } from '@/components/button'
import { Page } from '@/components/layout'
import { branchQrQuery, tableQuery } from '@/features/back-office/api'
import type { QrKind } from '@/features/back-office/qr-print-search'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { canManageBranch } from '@/lib/roles'

const ROUTE_ID = '/$restaurantSlug/$branchSlug/manage/tables/print'
const TABLES_PATH = '/$restaurantSlug/$branchSlug/manage/tables'

/**
 * One printable QR sign (PRD, G3): restaurant page, branch page, take-away ordering, or a table.
 * The app chrome is `print:hidden`; a normal printer is the medium (T14: thermal paper fades).
 */
export function QrPrintPage() {
  const t = useT()
  const { restaurantSlug, branchSlug, restaurantId, branchId, context } = useManagedBranch()
  const search = useSearch({ from: ROUTE_ID })
  const allowed = context !== undefined && canManageBranch(context.role)
  const wantsTable = search.kind === 'table'
  const qr = useQuery({ ...branchQrQuery(restaurantId, branchId), enabled: allowed && !wantsTable })
  const table = useQuery({
    ...tableQuery(restaurantId, branchId, search.table ?? ''),
    enabled: allowed && wantsTable && search.table !== undefined,
  })

  const back = (
    <Link to={TABLES_PATH} params={{ restaurantSlug, branchSlug }} search={{ page: 1 }} className="text-accent-ink font-medium">
      {t('qr.back')}
    </Link>
  )
  if (!allowed) {
    return (
      <Page width="sm" className="flex flex-col gap-4">
        <p role="alert" className="text-muted text-sm">
          {t('tables.adminOnly')}
        </p>
        {back}
      </Page>
    )
  }

  const branch = context.branches.find((candidate) => candidate.id === branchId)
  const sign = signFor(search.kind, {
    restaurantUrl: qr.data?.restaurant_url,
    branchUrl: qr.data?.branch_url,
    takeAwayUrl: qr.data?.take_away_url,
    tableName: table.data?.name,
    tableUrl: table.data?.url,
  })
  const pending = wantsTable ? table.isPending && search.table !== undefined : qr.isPending
  const missing = !pending && sign === undefined

  return (
    <Page width="sm" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <h1 className="text-xl font-semibold">{t('qr.title')}</h1>
        {back}
      </div>
      {pending && <p className="text-muted text-sm print:hidden">{t('common.loading')}</p>}
      {missing && (
        <p role="alert" className="text-muted text-sm">
          {t('qr.missing')}
        </p>
      )}
      {sign && (
        <>
          <section
            aria-label={t('qr.title')}
            className="border-border flex flex-col items-center gap-4 rounded-xl border bg-white p-6 text-center text-black print:border-0 print:p-0"
          >
            <p className="text-2xl font-semibold">{context.settings.name}</p>
            {branch && <p className="text-lg">{branch.name}</p>}
            {sign.kind === 'table' && <p className="text-4xl font-bold">{sign.tableName}</p>}
            <QRCodeSVG
              value={sign.url}
              size={512}
              level="M"
              marginSize={2}
              role="img"
              aria-label={t('qr.image', { url: sign.url })}
              className="h-auto w-64 max-w-full print:w-[80mm]"
            />
            <p className="text-lg">{t(`qr.sign.${sign.kind}`)}</p>
            <p className="font-mono text-xs break-all">{sign.url}</p>
          </section>
          <Button onClick={() => window.print()} className="self-start print:hidden">
            {t('qr.print')}
          </Button>
        </>
      )}
    </Page>
  )
}

interface Sign {
  kind: QrKind
  url: string
  tableName?: string
}

/** What the sign says, once its URL has loaded; undefined while missing (or a table that is gone). */
function signFor(
  kind: QrKind,
  loaded: {
    restaurantUrl: string | undefined
    branchUrl: string | undefined
    takeAwayUrl: string | undefined
    tableName: string | undefined
    tableUrl: string | undefined
  },
): Sign | undefined {
  switch (kind) {
    case 'restaurant':
      return loaded.restaurantUrl === undefined ? undefined : { kind, url: loaded.restaurantUrl }
    case 'branch':
      return loaded.branchUrl === undefined ? undefined : { kind, url: loaded.branchUrl }
    case 'take_away':
      return loaded.takeAwayUrl === undefined ? undefined : { kind, url: loaded.takeAwayUrl }
    case 'table':
      return loaded.tableUrl === undefined || loaded.tableName === undefined
        ? undefined
        : { kind, url: loaded.tableUrl, tableName: loaded.tableName }
  }
}
